import { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box } from '@mui/material';

import useDialog from 'store/dialog';
import useSport, { SportEntity } from 'store/sport';
import SectionLoader from 'components/sections/SectionLoader/SectionLoader';

import { SportService } from 'api/sport';

import useScreen from 'hooks/useScreen';

import { getFooterHeight, getHeaderHeight } from 'components/sections/Navigation/utils';
import useUpdate from 'hooks/useUpdate';
import MobileFooter from './MobileFooter';

interface BetByProps {
  sportId: string;
}

declare global {
  interface Window {
    BTRenderer;
  }
}

const BetBy: FC<BetByProps> = ({ sportId }) => {
  const { isMobile } = useScreen();
  const [, dialogActions] = useDialog();

  const [sportState] = useSport();

  const bt = useRef(null);
  const [isLoading, setIsLoading] = useState(!window.BTRenderer);

  const entity = sportState?.data?.[sportId] || ({} as SportEntity);

  const [searchParams] = useSearchParams();
  const btPath = searchParams.get('bt-path') || '/';
  const [btUrl, setBtUrl] = useState(btPath);

  useEffect(() => {
    if (bt.current && btPath !== btUrl) {
      setBtUrl(btPath);
      bt.current.updateOptions({
        url: btPath,
      });
    }
  }, [btUrl, btPath, bt.current]);

  const onSessionRefresh = () => {
    if (!bt.current) return;
    onInitialize();
  };

  const handleTokenExpired = async () => {
    const response = await SportService.getSportUrl(Number(sportId));

    return response?.[Number(sportId)]?.token;
  };

  const onInitialize = () => {
    bt.current?.kill();
    bt.current = null;
    bt.current = new window.BTRenderer().initialize({
      lang: entity.lang,
      betslipZIndex: 100,
      token: entity.token || null,
      brand_id: entity.brandId,
      themeName: entity.themeName,
      target: document.getElementById('betby'),
      onLogin: () => dialogActions.set('signIn'),
      onRegister: () => dialogActions.set('signUp'),
      onRecharge: () => dialogActions.set('deposit'),
      onTokenExpired: handleTokenExpired,
      onSessionRefresh,
      betSlipOffsetTop: 120,
      stickyTop: getHeaderHeight(),
      ...(isMobile
        ? {
            betSlipOffsetBottom: getFooterHeight(),
          }
        : {
            betSlipOffsetRight: 16,
            betSlipOffsetBottom: 16,
          }),
    });
  };

  useEffect(() => {
    if (entity?.url) {
      if (window.BTRenderer) {
        onInitialize();
      } else {
        const script = document.createElement('script');

        script.id = 'betByScript';
        script.src = entity?.url;
        script.async = true;

        script.onload = () => {
          onInitialize();
          setIsLoading(false);
        };

        script.onerror = (error) => {
          console.error('💥 BTRenderer initialize:', error);
        };

        document.body.appendChild(script);
      }
    }

    return () => {
      bt?.current?.kill();
    };
  }, [entity?.url]);

  useUpdate(() => {
    if (window.BTRenderer) {
      onInitialize();
    }
  }, [entity.lang, entity?.token]);

  return (
    <>
      <Box width="100%" minHeight="calc(100vh - 100px)">
        {isLoading && <SectionLoader />}
        <Box id="betby" />
        {isMobile && <MobileFooter url={btPath} />}
      </Box>
    </>
  );
};

export default BetBy;
